import { InlineLoader } from 'cat-ecommerce-alloy';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  FITMENT_VARIANTS as VARIANTS,
  STATUS
} from '../../../../constants/commonConstants';
import { productShape } from '../../../../hooks/useAnalytics/useAnalytics.proptypes';
import { isEmpty } from '../../../../utils';
import Availability from '../../Availability';
import Conditional from '../../Conditional';
import ProductFitment from '../../ProductFitment';
import ProductLink from '../ProductLink';
import ProductOverviewPriceDetails from './ProductOverviewPriceDetails';

const ProductOverview = ({
  brand,
  children,
  currency,
  fitmentEnabled,
  formattedZeroPrice,
  gaListName,
  name,
  partNumber,
  productClickDetails,
  productId,
  url,
  isFitmentLoading,
  didFitmentCallResolve,
  getFitmentResult,
  priceCallStatus
}) => {
  const loader = <InlineLoader className="my-2" />;
  const availability = useSelector(
    s => s.products.priceAndAvailability?.[productId]?.availability
  );
  const enableAddToCart = useSelector(
    s => s.products.priceAndAvailability?.[productId]?.enableAddToCart
  );
  const indirectReplacement = useSelector(
    s => s.products.priceAndAvailability?.[productId]?.indirectReplacement
  );
  const price = useSelector(
    s => s.products.priceAndAvailability?.[productId]?.discountedPrice
  );
  const unitPrice = useSelector(
    s => s.products.priceAndAvailability?.[productId]?.unitPrice
  );
  const replacementParts = useSelector(
    s => s.products.priceAndAvailability?.[productId]?.replacementParts
  );
  const taxDisclaimerMessageByCustomer = useSelector(
    s => s.orders?.taxDisclaimerMessage
  );

  const isReplacedPart = !isEmpty(replacementParts) || indirectReplacement;
  const isZeroPrice = price === formattedZeroPrice;
  const isPriceLoading = priceCallStatus === STATUS.PENDING;
  const showPriceAndAvailability =
    (enableAddToCart && price && !isZeroPrice && !isReplacedPart) ||
    isPriceLoading;
  const serialNumber = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.serialNumber
  );

  return (
    <div className="w-100">
      <p>
        <ProductLink
          className="text-xl text-univers--bold u-color--ExtraDarkGray"
          link={url}
          productClickDetails={productClickDetails}
          gaListName={gaListName}
        >
          {name}
        </ProductLink>
      </p>
      {brand && <p className="text-sm">{brand}</p>}
      <Conditional test={!isPriceLoading} fallback={loader}>
        {showPriceAndAvailability && (
          <ProductOverviewPriceDetails
            className="mt-2"
            currency={currency}
            taxDisclaimerMessage={taxDisclaimerMessageByCustomer}
            price={price}
            unitPrice={unitPrice}
          />
        )}
        {children && <div className="mt-2">{children}</div>}
        {availability && (
          <div className="mt-2">
            <Availability
              availabilityInformation={availability}
              forceShowAvailabilityByDate
              hideLabel
              showAdditionalInfo
            />
          </div>
        )}
      </Conditional>
      {fitmentEnabled && !!serialNumber && (
        <Conditional test={!isFitmentLoading} fallback={loader}>
          <ProductFitment
            className="my-2"
            partNumber={partNumber}
            variant={VARIANTS.small}
            didFitmentCallResolve={didFitmentCallResolve}
            getFitmentResult={getFitmentResult}
          />
        </Conditional>
      )}
    </div>
  );
};

ProductOverview.propTypes = {
  brand: PropTypes.string,
  children: PropTypes.node,
  currency: PropTypes.string,
  fitmentEnabled: PropTypes.bool,
  formattedZeroPrice: PropTypes.string,
  gaListName: PropTypes.string,
  name: PropTypes.string.isRequired,
  partNumber: PropTypes.string,
  productClickDetails: PropTypes.arrayOf(productShape),
  productId: PropTypes.string,
  url: PropTypes.string,
  isFitmentLoading: PropTypes.bool,
  didFitmentCallResolve: PropTypes.bool,
  getFitmentResult: PropTypes.func,
  priceCallStatus: PropTypes.string.isRequired
};

export default ProductOverview;
